import { useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import { Select, randomTilt } from "./Components/Select.tsx";
import { RangeSlider } from "./Components/RangeSlider.tsx";
import { SliderHandle } from "./Components/SliderHandle.tsx";
import PremiumInfo from "./Components/PremiumInfo/PremiumInfo";

export interface AIRoomSettingsValues {
  craziness: number;
  sexualScale: number;
  sipsMin: number;
  sipsMax: number;
  chug: number; // 0 = none, 1 = some, 2 = lots
  shots: number;
}

// Mirrors SIPS_MIN / SIPS_MAX in server/aiRoomPrompt.js
const SIPS_MIN = 1;
const SIPS_MAX = 15;

interface AIRoomSettingsProps {
  roomName: string;
  sessionId: string;
  socket: any;
  initialSettings: AIRoomSettingsValues;
  initialStep?: number;
  onDone: () => void;
  onBack: () => void;
  showInfoMessage?: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
  ) => void;
}

function AIRoomSettings({
  roomName,
  sessionId,
  socket,
  initialSettings,
  initialStep = 0,
  onDone,
  onBack,
  showInfoMessage,
}: AIRoomSettingsProps) {
  const { t } = useTranslation();

  const [settings, setSettings] = useState(initialSettings);
  const [step, setStep] = useState(initialStep); // 0 = craziness+sexual, 1 = drinks
  // The percent sticker and the slider thumb share an angle; both get a new
  // one, and the sticker pops again, on each release
  const [percentTilt, setPercentTilt] = useState(randomTilt);
  const [sexualGrabbed, setSexualGrabbed] = useState(false);
  const commitSexualScale = (value: number) => {
    setSexualGrabbed(false);
    setPercentTilt((prev) => randomTilt(prev));
    apply({ sexualScale: value });
  };

  // Every selection is saved immediately (fire-and-forget), as the pre-rework
  // setup wizard did; NESTE only moves on.
  const apply = (change: Partial<AIRoomSettingsValues>) => {
    const next = { ...settings, ...change };
    setSettings(next);
    socket.emit("ai room set settings", { roomName, sessionId, ...next });
  };

  // "Dere får 50 % seksuelt innhold." with "50 %" as a sticker. The % sign
  // and its spacing come from the locale string.
  const percentSentence = (percent: number) => {
    const MARK = "@@";
    const [before, rest = ""] = t("ai.settings.sexualHint", {
      percent: MARK,
    }).split(MARK);
    const sign = rest.match(/^\s?%/)?.[0] ?? "";
    return (
      <>
        {before}
        <span
          key={percentTilt}
          className="value-sticker"
          style={{ "--tilt": percentTilt } as React.CSSProperties}
        >
          {percent}
          {sign}
        </span>
        {rest.slice(sign.length)}
      </>
    );
  };

  const amountOptions = [
    { label: t("ai.settings.amount.none"), value: 0 },
    { label: t("ai.settings.amount.some"), value: 1 },
    { label: t("ai.settings.amount.lots"), value: 2 },
  ];

  const amountRow = (
    title: string,
    current: number,
    onSelect: (value: number) => void,
  ) => (
    <div className="settings-section">
      <p className="text-h3 text-color-black settings-title">{title}</p>
      <div className="settings-options settings-options-row" role="radiogroup">
        {amountOptions.map((opt) => (
          <Select
            key={opt.value}
            label={opt.label}
            value={opt.value}
            current={current}
            onSelect={onSelect}
            small
          />
        ))}
      </div>
    </div>
  );

  // Selections are already saved as they happen; NESTE only moves on.
  const submit = () => onDone();

  const topBar = (onBackClick: () => void) => (
    <div className="container-row lift-topbar">
      <div className="container-row-start">
        <div
          className="button-tertiary-m button-text-m text-color-black"
          onClick={onBackClick}
          style={{ maxHeight: "32.5px" }}
        >
          {t("back")}
        </div>
      </div>
      <div className="text-h3 text-color-black left-margin-s text-force-break">
        {roomName}
      </div>
    </div>
  );

  if (step === 0) {
    return (
      <div className="App-header">
        {topBar(onBack)}

        <div className="settings-body">
          <div className="settings-section settings-section-first">
            <p className="text-h2 text-color-black settings-title">
              {t("ai.settings.crazinessTitle")}
            </p>
            <div className="settings-options" role="radiogroup">
              {[
                { label: t("ai.settings.crazy.calm"), value: 1 },
                { label: t("ai.settings.crazy.medium"), value: 2 },
                { label: t("ai.settings.crazy.high"), value: 3 },
                { label: t("ai.settings.crazy.extreme"), value: 4 },
              ].map((opt) => (
                <Select
                  key={opt.value}
                  label={opt.label}
                  value={opt.value}
                  current={settings.craziness}
                  onSelect={(craziness) => apply({ craziness })}
                />
              ))}
            </div>
          </div>

          <div className="settings-section settings-section-fixed">
            <p className="text-h2 text-color-black settings-title">
              {t("ai.settings.sexualTitle")}
            </p>
            <div className="slider-wrap">
              <input
                className="outset-slider"
                type="range"
                min={0}
                max={100}
                step={1}
                value={settings.sexualScale}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    sexualScale: Number(e.target.value),
                  })
                }
                onPointerDown={() => setSexualGrabbed(true)}
                onMouseUp={(e) =>
                  commitSexualScale(Number(e.currentTarget.value))
                }
                onTouchEnd={(e) =>
                  commitSexualScale(Number(e.currentTarget.value))
                }
                onPointerCancel={() => setSexualGrabbed(false)}
              />
              <SliderHandle
                share={settings.sexualScale / 100}
                label={`${Math.round(settings.sexualScale)}%`}
                tilt={percentTilt}
                grabbed={sexualGrabbed}
              />
            </div>
            <p
              className="text-h4 text-color-black settings-slider-labels"
              style={{ marginTop: 8 }}
            >
              {settings.sexualScale === 0
                ? t("ai.settings.sexualHintNone")
                : percentSentence(Math.round(settings.sexualScale))}
            </p>
          </div>
        </div>

        <PremiumInfo
          roomName={roomName}
          source="settings"
          showInfoMessage={showInfoMessage}
        />
        <div
          className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center settings-next"
          onClick={() => setStep(1)}
        >
          {t("next")}
        </div>
      </div>
    );
  }

  // Step 1: Drinks
  return (
    <div className="App-header">
      {topBar(() => setStep(0))}

      <div className="settings-body">
        <div className="settings-section settings-section-first settings-section-fixed">
          <p
            className="text-h2 text-color-black settings-title"
            style={{ lineHeight: 1.1 }}
          >
            {t("ai.settings.drinkTitle")}
          </p>
          <div
            className="text-h3 text-color-black settings-title"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <span>{t("ai.settings.sipsTitle")}</span>
            <span>
              {settings.sipsMin === settings.sipsMax
                ? settings.sipsMin
                : `${settings.sipsMin}–${settings.sipsMax}`}
            </span>
          </div>
          <RangeSlider
            min={SIPS_MIN}
            max={SIPS_MAX}
            low={settings.sipsMin}
            high={settings.sipsMax}
            onCommit={(sipsMin, sipsMax) => apply({ sipsMin, sipsMax })}
          />
        </div>
        {amountRow(t("ai.settings.chugTitle"), settings.chug, (chug) =>
          apply({ chug }),
        )}
        {amountRow(t("ai.settings.shotsTitle"), settings.shots, (shots) =>
          apply({ shots }),
        )}
      </div>

      <PremiumInfo
        roomName={roomName}
        source="drinks"
        showInfoMessage={showInfoMessage}
      />
      <div
        className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center settings-next"
        onClick={submit}
      >
        {t("next")}
      </div>
    </div>
  );
}

export default AIRoomSettings;
